import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useQueryFonts } from '../hooks/useQueryFonts'
import type { TextElement } from '../../domain/canvas/entities/element'
import { fontLoader, useFontLoader } from '../../infrastructure/services/fontLoaderService'
import { sortFonts } from '../../domain/canvas/utils/fontDisplay'
import type { Font } from '../../domain/canvas/value-objects/font'
import { useIntlayer } from 'react-intlayer'

type FontFamilyProps = {
  fontFamily: TextElement['fontFamily']
  fontWeight?: number
  fontItalic?: boolean
  onChange: (fontFamily: string, weight: number, italic: boolean) => void
}

const renderFontPreview = (font: Font, noPreviewText: string, uniqueKey: string) => {
  // Render the SVG preview
  if (font.preview && font.preview.startsWith('data:image/svg+xml')) {
    return (
      <div className="flex items-center justify-start h-full overflow-hidden">
        <img
          key={uniqueKey}
          src={font.preview}
          alt={font.name}
          className="h-full object-contain object-left"
          style={{ transform: 'scale(2)', transformOrigin: 'left center' }}
        />
      </div>
    )
  }

  // Fallback if no preview available
  return (
    <div className="flex items-center justify-center h-full text-gray-400">
      <span className="text-sm">{noPreviewText}</span>
    </div>
  )
}

export const FontFamily = ({
  fontFamily,
  fontWeight = 400,
  fontItalic = false,
  onChange,
}: FontFamilyProps) => {
  const { loadingFonts, noPreview } = useIntlayer('fontFamily')
  const ref = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const fonts = useQueryFonts()

  // Find the selected font by matching name, weight, and italic
  // For backward compatibility, default to weight 400 and italic false if not specified
  const selectedFont = fonts?.find(
    (f) =>
      f.name === fontFamily &&
      f.weight === (fontWeight || 400) &&
      f.italic === (fontItalic || false),
  )

  // Load the selected font
  useFontLoader(
    selectedFont?.name || null,
    selectedFont?.url || null,
    selectedFont?.weight || 400,
    selectedFont?.italic || false,
  )

  // Handle click outside to close dropdown
  const handleClickOutside = useCallback((event: globalThis.MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }, [])

  // Add/remove click outside listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  const handleSelect = async (event: MouseEvent<HTMLLIElement>, font: Font) => {
    event.stopPropagation()
    // Check if this is a different font
    const isDifferent =
      font.name !== fontFamily ||
      font.weight !== (fontWeight || 400) ||
      font.italic !== (fontItalic || false)

    if (isDifferent) {
      // Load the font before selecting it
      try {
        await fontLoader.loadFont(font.name, font.url, font.weight, font.italic)
        onChange(font.name, font.weight, font.italic)
      } catch (error) {
        console.error(`Failed to load font ${font.name}:`, error)
        // Still change the font even if loading fails - fallback will be used
        onChange(font.name, font.weight, font.italic)
      }
    } else {
      // Even if same font, call onChange to notify parent of user interaction
      onChange(font.name, font.weight, font.italic)
    }
    setIsOpen(false)
  }

  // Show loading state only while fonts are actually loading
  if (!fonts) {
    return (
      <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
        <span className="text-gray-500">{loadingFonts}</span>
      </div>
    )
  }

  // If font is not found in the list, create a placeholder
  const displayFont = selectedFont || {
    name: fontFamily,
    preview: '',
    url: '',
    weight: fontWeight,
    italic: fontItalic,
  }

  // Sort fonts for display
  const sortedFonts = fonts ? sortFonts(fonts) : []

  // Create unique key for forcing re-render when font changes
  const displayFontKey = `${displayFont.name}-${displayFont.weight}-${displayFont.italic}`

  return (
    <div ref={ref} className="relative">
      <div
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
      >
        <div className="h-12 flex items-center">
          {renderFontPreview(displayFont, noPreview, displayFontKey)}
        </div>
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
          {sortedFonts.map((font) => {
            const isSelected =
              font.name === fontFamily &&
              font.weight === (fontWeight || 400) &&
              font.italic === (fontItalic || false)
            const isLoaded = fontLoader.isFontLoaded(font.name, font.weight, font.italic)
            const fontKey = `${font.name}-${font.weight}-${font.italic}`

            return (
              <li
                key={fontKey}
                className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                  isSelected ? 'bg-blue-100 hover:bg-blue-100' : ''
                }`}
                onClick={(e) => handleSelect(e, font)}
              >
                <div className="h-10 flex items-center justify-between">
                  <div className="flex-1">{renderFontPreview(font, noPreview, fontKey)}</div>
                  {isLoaded && <span className="ml-2 text-xs text-green-600">✓</span>}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
